import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Icon from "@mdi/react";
import { mdiAutoFix, mdiEye, mdiPlus, mdiPuzzleOutline } from "@mdi/js";
import { Grid, Button, SpeedDial, CircularProgress } from "@mui/material";
import { MainLayout } from "layouts";
import styles from "./styles.module.scss";
import { clsx } from "@ismael1361/utils";
import { NotistackServices, McpLibraryServices, PublicMcpDTO } from "shared/services";
import { HandleError } from "shared/utils";
import { HireMemberModal } from "components";

const DEFAULT_CATEGORIES = ["Todos"];

/** Formata o preço como "R$ X/mês" usando o menor valor disponível no pricingModel */
function formatPrice(pm: PublicMcpDTO["pricingModel"]): string {
	const values = [pm?.junior, pm?.pleno, pm?.senior].filter((v): v is number => typeof v === "number" && v > 0);
	if (values.length === 0) return "Consultar";
	const min = Math.min(...values);
	return `R$ ${min.toLocaleString("pt-BR", { minimumFractionDigits: 0 })}/mês`;
}

export const LibraryPage = () => {
	const [mcps, setMcps] = useState<PublicMcpDTO[]>([]);
	const [loading, setLoading] = useState(true);
	const [selectedCategory, setSelectedCategory] = useState("Todos");
	const [hiringMcp, setHiringMcp] = useState<PublicMcpDTO | null>(null);
	const time = useRef<NodeJS.Timeout>(undefined);

	const loadMcps = useCallback(() => {
		setLoading(true);
		clearTimeout(time.current);
		time.current = setTimeout(async () => {
			try {
				const data = await McpLibraryServices.listMcps();
				setMcps(data);
			} catch (error) {
				NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao carregar MCPs");
			} finally {
				setLoading(false);
			}
		}, 300);
	}, []);

	useEffect(() => {
		loadMcps();
	}, [loadMcps]);

	const categories = useMemo(() => {
		const cats = mcps
			.map((m) => m.category)
			.map((c) => c.split(",").map((s) => s.trim()))
			.reduce((acc, arr) => [...acc, ...arr], [])
			.filter((c) => c);
		return [...DEFAULT_CATEGORIES, ...Array.from(cats).sort()].filter((c, i, arr) => arr.indexOf(c) === i);
	}, [mcps]);

	const filteredMcps = useMemo(() => {
		if (selectedCategory === "Todos") {
			return mcps;
		}
		return mcps.filter((m) =>
			m.category
				.split(",")
				.map((s) => s.trim())
				.includes(selectedCategory),
		);
	}, [selectedCategory, mcps]);

	const handleFeatureInDevelopment = () => {
		NotistackServices.showWarningMessage("Funcionalidade em Desenvolvimento");
	};

	return (
		<MainLayout>
			<div className={styles.container}>
				<div className={styles.header}>
					<h2 className={styles.title}>Contratar Novos Membros</h2>

					<div className={styles.filtersContainer}>
						<div className={styles.filterChips}>
							{categories.map((category) => (
								<Button
									key={category}
									className={clsx(styles.filterChip, selectedCategory === category ? styles.active : styles.inactive)}
									onClick={() => setSelectedCategory(category)}
									disableRipple
								>
									{category}
								</Button>
							))}
						</div>
					</div>
				</div>

				{loading ? (
					<div className={styles.loadingState}>
						<CircularProgress
							color="inherit"
							size={24}
						/>
						<span>Carregando MCPs...</span>
					</div>
				) : filteredMcps.length === 0 ? (
					<div className={styles.loadingState}>
						<Icon
							path={mdiPuzzleOutline}
							size={2}
						/>
						<span>Nenhum MCP encontrado.</span>
					</div>
				) : (
					<Grid
						container
						spacing={3}
					>
						{filteredMcps.map((mcp) => (
							<Grid
								size={{
									xs: 12,
									sm: 6,
									md: 4,
									lg: 3,
								}}
								key={mcp.id}
							>
								<div className={styles.card}>
									<div className={styles.cardImageContainer}>
										{mcp.iconUrl ? (
											<div
												className={styles.cardImage}
												style={{ backgroundImage: `url('${mcp.iconUrl}')` }}
											/>
										) : (
											<div className={clsx(styles.cardImage, styles.cardImagePlaceholder)}>
												<Icon
													path={mdiPuzzleOutline}
													size={3}
												/>
											</div>
										)}
										<div className={styles.hoverOverlay}>
											<button
												className={styles.actionButton}
												title="Ver Perfil"
												onClick={handleFeatureInDevelopment}
												disabled
											>
												<Icon
													path={mdiEye}
													size={1}
												/>
											</button>
											<button
												className={clsx(styles.actionButton, styles.primary)}
												title="Contratar"
												onClick={() => setHiringMcp(mcp)}
											>
												<Icon
													path={mdiPlus}
													size={1}
												/>
											</button>
										</div>
									</div>
									<div className={styles.cardContent}>
										<span className={styles.memberName}>{mcp.name}</span>
										<span className={styles.memberRole}>{mcp.description || mcp.category}</span>

										<div className={styles.cardFooter}>
											<div className={styles.skills}>
												{mcp.deliverableTypes?.slice(0, 3).map((dt, i) => (
													<span
														key={i}
														className={styles.skillTag}
													>
														{dt}
													</span>
												))}
											</div>
											<div className={styles.price}>{formatPrice(mcp.pricingModel)}</div>
										</div>
									</div>
								</div>
							</Grid>
						))}
					</Grid>
				)}

				<div className={styles.floatingAction}>
					<SpeedDial
						ariaLabel=""
						icon={
							<Icon
								path={mdiAutoFix}
								size={1.2}
							/>
						}
						onClick={handleFeatureInDevelopment}
					/>
				</div>
			</div>

			<HireMemberModal
				open={!!hiringMcp}
				mcp={hiringMcp}
				onClose={() => setHiringMcp(null)}
				onHired={() => {}}
			/>
		</MainLayout>
	);
};
